// src/components/ShopifyBillingTest.js — « Abonnement » (/billing, reached from
// Paramètres › « Gérer la facturation »; Shopify sends the owner back to
// /billing-success or /billing-failed after a charge).
// The price grid is the website's (billingPricing.js, ported from
// UnanimityWebV2/src/pricing.ts — Kevin 29/10); the account's own estimate
// comes from its tickets created over the last 30 days (GET
// /api/tickets/dashboard-insights?window_days=30, kpis.created_7d = the
// window's count, e-mails that are not requests excluded).
// No self-serve plan checkout next to the grid: the old plan buttons started
// Shopify subscriptions at 99 / 199 / 399 USD, not the grid's prices. The way
// in is a quote: POST /api/shopify/submit_custom_request (GET
// /check_custom_request says one is pending); once Unanimity has activated the
// account's offer (users.custom_plan = 'Activated'), it is accepted through
// Shopify: POST /api/shopify/create_custom_shopify_subscription.
// The current subscription (GET /api/users/user) stays visible; cancelling:
// DELETE /api/shopify/delete_shopify_subscription, behind a confirmation.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calculator, Check, CreditCard, Mail, Receipt, Send, Sparkles, Ticket, XCircle,
} from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, DescriptionList, EmptyState, Field, Kpi, Modal, OverflowMenu, PageHeader, Toast,
} from '../design/ui';
import { formatShortDate } from '../utils/dates';
import {
  BASE_PRICE, INCLUDED_TICKETS, QUOTE_THRESHOLD, estimateFor, gridRows,
} from './billingPricing';
import './ShopifyBillingTest.css';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

// « 28 oct. » — the shared short format (utils/dates: naive UTC, Paris time).
const formatDate = (value) => formatShortDate(value, undefined, { empty: null });

const ShopifyBillingTest = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [volume, setVolume] = useState(undefined); // undefined = loading, null = unavailable
  const [processing, setProcessing] = useState(null); // 'custom' | 'cancel' | 'request' while a call runs
  const [error, setError] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestText, setRequestText] = useState('');
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [toast, setToast] = useState(null);

  const locale = String(i18n?.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const euros = (value, digits = 0) => new Intl.NumberFormat(locale, {
    style: 'currency', currency: 'EUR', minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(value);
  const count = (value) => new Intl.NumberFormat(locale).format(value);

  const fetchUserData = async () => {
    try {
      if (!localStorage.getItem('access_token')) {
        setError(t('teamStatsUi.billing.notLogged', 'Vous n’êtes pas connecté.'));
        return;
      }
      const response = await axios.get('/api/users/user', { headers: authHeaders() });
      if (response.data) setUser(response.data);
    } catch (err) {
      console.error('Failed to fetch user data:', err?.response?.status);
      setError(t('teamStatsUi.billing.loadError', 'Votre abonnement n’a pas pu être chargé.'));
    } finally {
      setLoading(false);
    }
  };

  const checkCustomRequestStatus = async () => {
    try {
      const response = await axios.get('/api/shopify/check_custom_request', { headers: authHeaders() });
      setRequestSubmitted(Boolean(response.data?.request_exists));
    } catch (err) {
      console.error('Error checking custom request status:', err?.response?.status);
    }
  };

  // The account's volume: tickets created over the last 30 days, and the 30 before.
  const fetchVolume = async () => {
    try {
      const response = await axios.get('/api/tickets/dashboard-insights?window_days=30', { headers: authHeaders() });
      const created = response.data?.kpis?.created_7d;
      setVolume(typeof created?.current === 'number'
        ? { current: created.current, previous: typeof created.previous === 'number' ? created.previous : null }
        : null);
    } catch (err) {
      console.error('Failed to fetch the ticket volume:', err?.response?.status);
      setVolume(null);
    }
  };

  useEffect(() => {
    fetchUserData();
    checkCustomRequestStatus();
    fetchVolume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const returnState = location.pathname === '/billing-success'
    ? 'success'
    : location.pathname === '/billing-failed' ? 'failed' : null;

  const submitRequest = async (event) => {
    event.preventDefault();
    setProcessing('request');
    try {
      await axios.post('/api/shopify/submit_custom_request', { description: requestText }, { headers: authHeaders() });
      setRequestSubmitted(true);
      setRequestOpen(false);
      setToast({ tone: 'success', text: t('teamStatsUi.billing.requestSent', 'Demande envoyée : nous revenons vers vous rapidement avec une offre.') });
    } catch (err) {
      console.error('Error submitting custom request:', err?.response?.status);
      setToast({ tone: 'danger', text: t('teamStatsUi.billing.requestFailed', 'La demande n’a pas pu être envoyée. Réessayez.') });
    } finally {
      setProcessing(null);
    }
  };

  // The offer Unanimity prepared for this account, accepted on Shopify.
  const activateCustomOffer = async () => {
    if (!user?.shop_name) {
      setToast({ tone: 'danger', text: t('teamStatsUi.billing.noShop', 'Aucune boutique Shopify n’est rattachée au compte : l’abonnement passe par la facturation Shopify.') });
      return;
    }
    setProcessing('custom');
    setError(null);
    try {
      const response = await axios.post('/api/shopify/create_custom_shopify_subscription', { billing_interval: 'year' }, { headers: authHeaders() });
      window.location.href = response.data.confirmation_url;
    } catch (err) {
      console.error('Error creating the custom subscription:', err?.response?.status);
      setError(t('accountScopeUi.billing.customFailed', 'L’offre n’a pas pu être activée sur Shopify. Réessayez ou contactez-nous.'));
    } finally {
      setProcessing(null);
    }
  };

  const cancelSubscription = async () => {
    setProcessing('cancel');
    try {
      await axios.delete('/api/shopify/delete_shopify_subscription', { headers: authHeaders() });
      setConfirmCancel(false);
      setToast({ tone: 'success', text: t('teamStatsUi.billing.cancelled', 'Abonnement résilié : le compte repasse en formule gratuite.') });
      fetchUserData();
    } catch (err) {
      console.error('Error deleting subscription:', err?.response?.status);
      setConfirmCancel(false);
      setToast({ tone: 'danger', text: t('teamStatsUi.billing.cancelFailed', 'La résiliation n’a pas abouti. Réessayez ou contactez-nous.') });
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="u-page billing-v2" aria-busy="true">
        <PageHeader title={t('teamStatsUi.billing.title', 'Abonnement')} />
        <div className="u-well">
          <div className="u-kpi-row" aria-hidden="true">
            {[0, 1, 2].map((i) => <span key={i} className="u-skeleton billing-v2__kpi-skeleton" />)}
          </div>
          <div className="billing-v2__grid" aria-hidden="true">
            {[0, 1].map((i) => <span key={i} className="u-skeleton billing-v2__skeleton" />)}
          </div>
          <span className="billing-v2__sr">{t('billing.loading', 'Chargement...')}</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="u-page billing-v2">
        <PageHeader title={t('teamStatsUi.billing.title', 'Abonnement')} />
        <div className="u-well">
          <Banner tone="danger">{error || t('billing.noUserData', 'Aucune donnée utilisateur disponible.')}</Banner>
        </div>
      </div>
    );
  }

  const intervalLabel = (value) => (value === 'year'
    ? t('teamStatsUi.billing.yearly', 'Annuelle')
    : value === 'month' ? t('teamStatsUi.billing.monthly', 'Mensuelle') : null);
  const planNames = {
    Basic: t('billing.basic', 'Basique'),
    Standard: t('billing.standard', 'Standard'),
    Professional: t('billing.professional', 'Professionnel'),
    Enterprise: t('billing.enterprise', 'Entreprise'),
    Free: t('billing.free', 'Gratuit'),
  };
  const currentName = planNames[user.subscription_plan] || user.subscription_plan;
  const customOfferReady = user.custom_plan === 'Activated';
  const requestPending = requestSubmitted && !customOfferReady;
  const estimate = volume ? estimateFor(volume.current) : null;

  const subtitle = user.is_subscribed
    ? t('teamStatsUi.billing.subtitleActive', 'Formule {{plan}} · facturation {{interval}} par Shopify', {
      plan: currentName,
      interval: (intervalLabel(user.billing_interval) || '').toLowerCase() || t('billing.na', 'N/D'),
    })
    : t('accountScopeUi.billing.subtitle', 'Un prix au volume de tickets : {{base}} HT par mois avec {{included}} tickets inclus, puis un prix par ticket dégressif', {
      base: euros(BASE_PRICE), included: count(INCLUDED_TICKETS),
    });

  // The one way in next to the grid: a quote (no self-serve plan checkout).
  const headerAction = requestPending
    ? <Badge tone="info" dot>{t('accountScopeUi.billing.requestPendingBadge', 'Demande de devis envoyée')}</Badge>
    : customOfferReady ? null : (
      <button type="button" className="u-btn u-btn--primary" onClick={() => setRequestOpen(true)} disabled={Boolean(processing)}>
        <Mail aria-hidden="true" />
        {t('accountScopeUi.billing.requestQuote', 'Demander un devis')}
      </button>
    );

  const facts = [
    { label: t('teamStatsUi.billing.factPlan', 'Formule'), value: currentName || t('billing.noActiveSubscription', 'Aucun abonnement actif') },
    {
      label: t('teamStatsUi.billing.factStatus', 'Statut'),
      value: user.is_subscribed
        ? <Badge tone="success" dot>{t('billing.active', 'Actif')}</Badge>
        : <Badge>{t('billing.inactive', 'Inactif')}</Badge>,
    },
    { label: t('teamStatsUi.billing.factInterval', 'Facturation'), value: intervalLabel(user.billing_interval) },
    { label: t('teamStatsUi.billing.factStart', 'Depuis le'), value: user.is_subscribed ? formatDate(user.subscription_start_date) : null },
    { label: t('teamStatsUi.billing.factEnd', 'Période en cours jusqu’au'), value: user.is_subscribed ? formatDate(user.subscription_end_date) : null },
    { label: t('teamStatsUi.billing.factShop', 'Boutique Shopify'), value: user.shop_name || null },
  ];

  const rowLabel = (row) => {
    if (row.key === 'base') return t('accountScopeUi.billing.rowBase', 'Socle plateforme · {{included}} tickets inclus', { included: count(INCLUDED_TICKETS) });
    if (row.key === 'quote') return t('accountScopeUi.billing.rowOver', 'Plus de {{n}} tickets', { n: count(QUOTE_THRESHOLD) });
    return t('accountScopeUi.billing.rowRange', '{{from}} – {{to}} tickets', { from: count(row.from), to: count(row.to) });
  };
  const rowPrice = (row) => {
    if (row.key === 'base') return t('accountScopeUi.billing.perMonth', '{{amount}} HT / mois', { amount: euros(BASE_PRICE) });
    if (row.key === 'quote') return t('accountScopeUi.billing.onQuote', 'Sur devis');
    return t('accountScopeUi.billing.perTicket', '{{amount}} / ticket', { amount: euros(row.price, 2) });
  };

  const renderKpis = () => (
    <div className="u-kpi-row">
      <Kpi
        icon={CreditCard}
        tone={user.is_subscribed ? undefined : 'neutral'}
        label={t('teamStatsUi.billing.currentTitle', 'Votre abonnement')}
        value={user.is_subscribed ? currentName : t('accountScopeUi.billing.none', 'Aucun')}
        context={user.is_subscribed
          ? (formatDate(user.subscription_end_date)
            ? t('accountScopeUi.billing.activeUntil', 'actif jusqu’au {{date}}', { date: formatDate(user.subscription_end_date) })
            : t('billing.active', 'Actif'))
          : t('billing.noActiveSubscription', 'Aucun abonnement actif')}
      />
      {volume === undefined && <span className="u-skeleton billing-v2__kpi-skeleton" aria-hidden="true" />}
      {volume && (
        <Kpi
          icon={Ticket}
          tone="info"
          label={t('accountScopeUi.billing.kpiTickets', 'Tickets créés · 30 j')}
          value={count(volume.current)}
          context={volume.previous != null
            ? t('accountScopeUi.billing.kpiTicketsPrevious', '{{n}} les 30 jours d’avant', { n: count(volume.previous) })
            : t('accountScopeUi.billing.kpiTicketsScope', 'tickets créés, hors e-mails qui ne sont pas des demandes')}
        />
      )}
      {volume === undefined && <span className="u-skeleton billing-v2__kpi-skeleton" aria-hidden="true" />}
      {estimate && (
        <Kpi
          icon={Calculator}
          tone="success"
          label={t('accountScopeUi.billing.kpiEstimate', 'Estimation mensuelle')}
          value={estimate.onQuote ? t('accountScopeUi.billing.onQuote', 'Sur devis') : euros(estimate.total)}
          unit={estimate.onQuote ? undefined : t('accountScopeUi.billing.unitHt', 'HT / mois')}
          context={t('accountScopeUi.billing.estimateScope', 'estimation sur vos 30 derniers jours')}
        />
      )}
    </div>
  );

  const renderGrid = () => (
    <Card
      icon={Receipt}
      title={t('accountScopeUi.billing.gridTitle', 'Grille tarifaire')}
      description={t('accountScopeUi.billing.gridDesc', 'Le socle à {{base}} HT par mois inclut {{included}} tickets. Au-delà, le prix par ticket baisse avec le volume.', {
        base: euros(BASE_PRICE), included: count(INCLUDED_TICKETS),
      })}
      footerNote={t('accountScopeUi.billing.gridNote', 'Utilisateurs et transporteurs illimités, sans surcoût. Tarifs HT, mensuels.')}
      flush
    >
      <ul className="u-list billing-v2__tiers">
        {gridRows().map((row) => {
          const isYours = estimate && estimate.row === row.key;
          return (
            <li key={row.key} className={`u-row billing-v2__tier${isYours ? ' is-yours' : ''}`}>
              <div className="u-row__main">
                <div className="u-row__title">{rowLabel(row)}</div>
              </div>
              {isYours && <Badge tone="accent" dot>{t('accountScopeUi.billing.yourVolume', 'Votre volume')}</Badge>}
              <span className="billing-v2__tier-price u-num">{rowPrice(row)}</span>
            </li>
          );
        })}
      </ul>
    </Card>
  );

  const renderEstimate = () => {
    let body;
    if (volume === undefined) {
      body = <span className="u-skeleton billing-v2__estimate-skeleton" aria-hidden="true" />;
    } else if (!estimate) {
      body = (
        <EmptyState icon={Calculator} title={t('accountScopeUi.billing.noVolumeTitle', 'Le volume de vos tickets n’a pas pu être lu')}>
          {t('accountScopeUi.billing.noVolumeHint', 'La grille ci-contre reste la référence : demandez un devis pour une offre chiffrée.')}
        </EmptyState>
      );
    } else if (estimate.onQuote) {
      body = (
        <div className="billing-v2__estimate">
          <p className="billing-v2__total">{t('accountScopeUi.billing.onQuote', 'Sur devis')}</p>
          <p className="billing-v2__estimate-sub">
            {t('accountScopeUi.billing.onQuoteDesc', '{{volume}} tickets sur 30 jours : au-delà de {{n}} tickets par mois, le tarif est établi sur mesure.', {
              volume: count(estimate.tickets), n: count(QUOTE_THRESHOLD),
            })}
          </p>
        </div>
      );
    } else {
      body = (
        <div className="billing-v2__estimate">
          <p className="billing-v2__total">
            <span className="u-num">{euros(estimate.total)}</span>
            <span className="billing-v2__per">{t('accountScopeUi.billing.unitHt', 'HT / mois')}</span>
          </p>
          <p className="billing-v2__estimate-sub">
            {estimate.perTicket != null
              ? t('accountScopeUi.billing.perTicketAvg', 'soit {{amount}} HT par ticket pour {{n}} tickets', { amount: euros(estimate.perTicket, 2), n: count(estimate.tickets) })
              : t('accountScopeUi.billing.noTicket', 'aucun ticket créé sur la période : le socle seul')}
          </p>
          <ul className="billing-v2__breakdown">
            <li>
              <span>{t('accountScopeUi.billing.rowBase', 'Socle plateforme · {{included}} tickets inclus', { included: count(INCLUDED_TICKETS) })}</span>
              <b className="u-num">{euros(estimate.base)}</b>
            </li>
            {estimate.extraTickets > 0 && (
              <li>
                <span>{t('accountScopeUi.billing.extraTickets', '{{n}} tickets additionnels', { n: count(estimate.extraTickets) })}</span>
                <b className="u-num">{euros(estimate.extraCost)}</b>
              </li>
            )}
          </ul>
        </div>
      );
    }
    return (
      <Card
        icon={Calculator}
        title={t('accountScopeUi.billing.estimateTitle', 'Votre estimation')}
        description={t('accountScopeUi.billing.estimateDesc', 'Estimation sur vos 30 derniers jours, HT, à partir des tickets créés.')}
      >
        {body}
        <ul className="billing-v2__included" aria-label={t('accountScopeUi.billing.includedTitle', 'Toujours inclus')}>
          <li><Check aria-hidden="true" />{t('accountScopeUi.billing.included1', 'Utilisateurs et transporteurs illimités')}</li>
          <li><Check aria-hidden="true" />{t('accountScopeUi.billing.included2', 'Agents IA : réponses clients et réclamations transporteurs')}</li>
          <li><Check aria-hidden="true" />{t('accountScopeUi.billing.included3', 'Tous les canaux : e-mail, WhatsApp, réseaux sociaux, chatbot, ERP')}</li>
        </ul>
      </Card>
    );
  };

  return (
    <div className="u-page billing-v2">
      <PageHeader title={t('teamStatsUi.billing.title', 'Abonnement')} subtitle={subtitle} actions={headerAction} />

      <div className="u-well">
        {returnState === 'success' && (
          <Banner tone="success" title={t('teamStatsUi.billing.successTitle', 'Abonnement confirmé')}>
            {t('teamStatsUi.billing.successBody', 'Shopify a confirmé le paiement : votre formule est active.')}
          </Banner>
        )}
        {returnState === 'failed' && (
          <Banner tone="warning" title={t('teamStatsUi.billing.failedTitle', 'Le paiement n’a pas abouti')}>
            {t('accountScopeUi.billing.failedBody', 'Shopify n’a pas confirmé l’abonnement : il a été refusé ou annulé. Rien n’a été facturé.')}
          </Banner>
        )}
        {error && <Banner tone="danger">{error}</Banner>}
        {customOfferReady && (
          <Banner
            tone="info"
            title={t('accountScopeUi.billing.customReadyTitle', 'Votre offre personnalisée est prête')}
            actions={(
              <button type="button" className="u-btn u-btn--primary" onClick={activateCustomOffer} disabled={Boolean(processing)}>
                <Sparkles aria-hidden="true" />
                {processing === 'custom' ? t('billing.processing', 'Traitement...') : t('accountScopeUi.billing.customActivate', 'Activer l’offre')}
              </button>
            )}
          >
            {user.custom_price
              ? t('accountScopeUi.billing.customReadyPrice', 'Montant convenu : {{amount}} USD par an, facturé par Shopify. Vous confirmez le paiement sur Shopify, puis revenez ici.', {
                amount: count(user.custom_price),
              })
              : t('accountScopeUi.billing.customReadyBody', 'Vous confirmez le paiement sur Shopify, puis revenez ici.')}
          </Banner>
        )}
        {requestPending && (
          <Banner tone="info" title={t('accountScopeUi.billing.requestPendingTitle', 'Votre demande de devis est envoyée')}>
            {t('accountScopeUi.billing.requestPendingBody', 'Nous revenons vers vous rapidement avec une offre au tarif de la grille.')}
          </Banner>
        )}

        {renderKpis()}

        <div className="billing-v2__grid">
          {renderGrid()}
          {renderEstimate()}
        </div>

        <Card
          icon={CreditCard}
          title={t('teamStatsUi.billing.currentTitle', 'Votre abonnement')}
          description={user.is_subscribed
            ? t('teamStatsUi.billing.currentDesc', 'Payé par la facturation de votre boutique Shopify : les factures arrivent dans votre espace Shopify.')
            : t('accountScopeUi.billing.currentNone', 'Aucun abonnement en cours. Demandez un devis : l’offre suit la grille ci-dessus.')}
          actions={user.is_subscribed ? (
            <OverflowMenu
              label={t('teamStatsUi.billing.menu', 'Gérer l’abonnement')}
              items={[{ label: t('billing.cancelSubscription', 'Annuler l’abonnement'), icon: XCircle, danger: true, onClick: () => setConfirmCancel(true) }]}
            />
          ) : null}
        >
          <DescriptionList items={facts} />
        </Card>
      </div>

      {requestOpen && (
        <Modal
          icon={Mail}
          title={t('accountScopeUi.billing.requestQuote', 'Demander un devis')}
          description={t('accountScopeUi.billing.requestDesc', 'Dites-nous vos volumes et vos besoins : nous préparons une offre au tarif de la grille, puis vous l’activez ici.')}
          onClose={() => processing !== 'request' && setRequestOpen(false)}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setRequestOpen(false)} disabled={processing === 'request'}>{t('billing.cancel', 'Annuler')}</button>
              <button type="submit" form="billing-v2-request" className="u-btn u-btn--primary" disabled={processing === 'request' || !requestText.trim()}>
                <Send aria-hidden="true" />
                {t('teamStatsUi.billing.requestSubmit', 'Envoyer la demande')}
              </button>
            </>
          )}
        >
          <form id="billing-v2-request" className="u-form" onSubmit={submitRequest}>
            {estimate && (
              <p className="billing-v2__request-context">
                {estimate.onQuote
                  ? t('accountScopeUi.billing.requestContextQuote', 'Vos 30 derniers jours : {{n}} tickets.', { n: count(estimate.tickets) })
                  : t('accountScopeUi.billing.requestContext', 'Vos 30 derniers jours : {{n}} tickets, soit une estimation de {{amount}} HT par mois.', {
                    n: count(estimate.tickets), amount: euros(estimate.total),
                  })}
              </p>
            )}
            <Field label={t('teamStatsUi.billing.requestLabel', 'Votre besoin')} htmlFor="billing-v2-request-text">
              <textarea
                id="billing-v2-request-text"
                className="u-textarea"
                rows={5}
                value={requestText}
                onChange={(e) => setRequestText(e.target.value)}
                placeholder={t('accountScopeUi.billing.requestPlaceholder', 'Volume mensuel attendu, nombre de boutiques, canaux, date de démarrage…')}
                required
              />
            </Field>
          </form>
        </Modal>
      )}

      {confirmCancel && (
        <ConfirmDialog
          icon={XCircle}
          title={t('teamStatsUi.billing.cancelTitle', 'Résilier la formule {{plan}} ?', { plan: currentName })}
          message={t('teamStatsUi.billing.cancelMessage', 'L’abonnement Shopify est arrêté tout de suite et le compte repasse en formule gratuite (50 tickets par mois).')}
          confirmLabel={t('teamStatsUi.billing.cancelConfirm', 'Résilier')}
          cancelLabel={t('billing.cancel', 'Annuler')}
          onConfirm={cancelSubscription}
          onCancel={() => processing !== 'cancel' && setConfirmCancel(false)}
          busy={processing === 'cancel'}
        />
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default ShopifyBillingTest;
